import React, { Component } from "react";
import PropTypes from "prop-types";

/**
 * @class
 * @classdesc Base control.
 *
 * @param {object} props - Control properties
 * @param {function} [props.onConfig] - Call this function when control changes its configuration. This triggers ECS to update its state.
 * Functions parameters:
 *   - method - ECS method like "setCfgValue", "setSubCfgValue", ...
 *   - attrName - In case of "setCfgValue" and "setSubCfgValue": Name of the attribute
 *   - attrValue - In case of "setCfgValue" and "setSubCfgValue": Value of the attribute
 *   - options - Additional options.
 * For more info see the {@link HcsConfigurator#configure} function.
 * @param {object} [props.configWait] - Promise object which waits for a specifiy ECS event like "setCfgValue".
 * @param {bool|string|number} [props.progressState] - Current progress wait state. "false" is default, "indeterminate" when configurator is loading next ECS state.
 * @param {bool} [renderOuterListControl=true] - Normally every control has a surrounding LI element, because controls are listed inside an UL.
 * When its required to render a control in another context (like using it in other controls) return "false" to only render the control itself without LI.
 */
export default class HcsControl extends Component {
    constructor(props) {
        super(props);

        this.isVisible = this.isVisible.bind(this);
        this.isEnabled = this.isEnabled.bind(this);
        this.childClass = this.childClass.bind(this);
        this.triggerConfigurator = this.triggerConfigurator.bind(this);
    }

    /**
     * Returns elements visibility state.
     * @returns {boolean} Elements visibility state.
     */
    isVisible() {
        const { data } = this.props;

        return data.info.visible;
    }

    /**
     * Returns elements enabled state. This includes:
     *  - enabled state in ECS object
     *  - currently running progress spinner.
     * @returns {boolean} Elements enabled state.
     */
    isEnabled() {
        const { data, progressState } = this.props;

        return data.info.guienabled && progressState === false;
    }

    /**
     * Define CSS class(es) for outer LI element.
     * Can be overridden in child class to return special CSS classes for the container.
     * @returns {Array|string} Array of CSS class names or single string.
     */
    childClass() {
        return null;
    }

    /**
     * "Virtual" function to in subclass and return the necessary data item for CCAO.
     * @returns {any} Build data object for triggerConfigurator() function.
     */
    getCcaoData() {
        return null;
    }
        
    /**
     * Call from within subclasses when the control changes its value.
     * When the belonging ECS item contains a data object for CCAO it triggers
     * an event to listening configurator class.
     */
    triggerConfigurator() {
        const { hcs, data } = this.props;

        let ccaoData = this.getCcaoData();

        if(ccaoData && ccaoData.triggerEvent) {
            hcs.api.configTrigger({
                groupId: data.data.AGROUP,
                itemId: data.ident,
                data: ccaoData.triggerEvent
            });
        }
    }

    /**
     * Unique ECS ID for this control.
     * This is usually being delivered from ECS.
     * Override this function to return another value than this.
     */
    ecsKey() {
        return this.props.data.ident;
    }

    render(innerControl) {
        const { data, renderOuterListControl } = this.props;

        let classes = [ "hcs-it" ];
        let childClasses = this.childClass();

        if(data.info.focused) {
            classes.push("hcs-it-focused");
        }

        if(childClasses) {
            switch(true) {
                case typeof childClasses === "string":
                    classes.push(childClasses);
                    break;

                case Array.isArray(childClasses):
                    classes = classes.concat(childClasses);
                    break;
            }
        }

        return this.isVisible() ?
            (
                renderOuterListControl ?
                    <li ecs-key={this.ecsKey()} className={classes.join(" ")}>
                        {innerControl}
                    </li> :
                    <>
                        {innerControl}
                    </>
            ) : null;
    }
}

HcsControl.propTypes = {
    hcs: PropTypes.object,
    data: PropTypes.object,
    renderOuterListControl: PropTypes.bool,
    progressState: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.number
    ]),
    configWait: PropTypes.func
};

HcsControl.defaultProps = {
    renderOuterListControl: true,
    configWait: _ => { }
};